<template>
  <div
    id="app"
    class="h-100"
    :class="[skinClasses]"
  >
    <component :is="layout">
      <router-view />
    </component>
  </div>
</template>

<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from 'vue-demi'
import useAppConfig from '@core/app-config/useAppConfig'

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import { isUserLoggedIn } from '@/auth/utils'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')
const LayoutFullFirst = () => import('@/layouts/full/LayoutFullFirst.vue')

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    LayoutFullFirst,
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') {
        return 'layout-full'
      } if (this.$route.meta.layout === 'fullIndex') {
        return 'layout-full-first'
      }
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
  },
  beforeCreate() {
    // Set colors in theme
    const colors = [
      'primary',
      'secondary',
      'success',
      'info',
      'warning',
      'danger',
      'light',
      'dark',
    ]

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(
        `--${colors[i]}`,
        document.documentElement,
      ).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(
        useCssVar(
          `--breakpoint-${breakpoints[i]}`,
          document.documentElement,
        ).value.slice(0, -2),
      )
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    // Set Window Width in store
    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
  mounted() {
    if (isUserLoggedIn()) {
      this.checkSubscriptionHealth()
    }
  },
  methods: {
    normalizeAccountingProgram(program, fallback = null) {
      const normalized = String(program || '').toLowerCase()
      if (normalized === 'spiris' || normalized === 'fortnox') {
        return normalized
      }
      return fallback
    },

    getProgramLabel(program) {
      const normalized = this.normalizeAccountingProgram(program, 'fortnox')
      return normalized === 'spiris'
        ? this.$t('SignupPage.AccountingSystemSpiris')
        : this.$t('SignupPage.AccountingSystemFortnox')
    },

    getProgramFromHealthPayload(payload) {
      const directProgram = this.normalizeAccountingProgram(payload && payload.accounting_program)
      if (directProgram) return directProgram

      const subscriptions = Array.isArray(payload && payload.subscriptions)
        ? payload.subscriptions
        : []

      const affected = subscriptions.find(item => item && (item.status === 'connect-needed' || item.status === 'reconnect-needed'))
      const affectedProgram = this.normalizeAccountingProgram(affected && affected.accounting_program)
      if (affectedProgram) return affectedProgram

      const firstWithProgram = subscriptions.find(item => item && item.accounting_program)
      const firstProgram = this.normalizeAccountingProgram(
        firstWithProgram && firstWithProgram.accounting_program,
      )
      return firstProgram
    },

    async getProgramFromSubscriptionResponse() {
      try {
        const res = await this.$useJwt.subscription()
        const payload = (res && res.data && res.data.data) || (res && res.data) || []
        const subscriptions = Array.isArray(payload) ? payload : []
        const firstWithProgram = subscriptions.find(item => item && item.accounting_program)
        return this.normalizeAccountingProgram(
          firstWithProgram && firstWithProgram.accounting_program,
        )
      } catch (e) {
        return null
      }
    },

    async fetchAuthLink(program) {
      try {
        const res = await this.$useJwt.connectAccounting(program)
        const data = (res && res.data) || {}
        return data.auth_link || (data.data && data.data.auth_link) || null
      } catch (e) {
        return null
      }
    },

    async resolveAccountingConnectTarget(healthPayload) {
      const resolvedProgram = this.getProgramFromHealthPayload(healthPayload)
        || await this.getProgramFromSubscriptionResponse()
      const preferredProgram = this.normalizeAccountingProgram(resolvedProgram, 'fortnox')

      const candidates = [preferredProgram]
      if (preferredProgram !== 'fortnox') candidates.push('fortnox')
      if (preferredProgram !== 'spiris') candidates.push('spiris')

      for (let i = 0; i < candidates.length; i += 1) {
        const program = candidates[i]
        // Probe available endpoint so UI text and redirect target match backend capability.
        const authLink = await this.fetchAuthLink(program)
        if (authLink) {
          return { program, authLink }
        }
      }

      return { program: preferredProgram, authLink: null }
    },

    // Check accounting connection health and prompt user to connect/reconnect
    async checkSubscriptionHealth() {
      try {
        const res = await this.$useJwt.subscriptionHealth()
        if (res && res.status === 401) return
        const payload = (res && res.data && res.data.data) || (res && res.data) || {}
        const { status } = payload
        if (status === 'connect-needed' || status === 'reconnect-needed') {
          const isReconnect = status === 'reconnect-needed'
          const { program, authLink } = await this.resolveAccountingConnectTarget(payload)
          const programLabel = this.getProgramLabel(program)
          const title = isReconnect
            ? this.$t('ConnectAccounting.ReconnectTitle', { program: programLabel })
            : this.$t('ConnectAccounting.ConnectTitle', { program: programLabel })
          const message = isReconnect
            ? this.$t('ConnectAccounting.ReconnectMessage', { program: programLabel })
            : this.$t('ConnectAccounting.ConnectMessage', { program: programLabel })
          const okTitle = isReconnect
            ? this.$t('ConnectAccounting.ReconnectButton', { program: programLabel })
            : this.$t('ConnectAccounting.ConnectButton', { program: programLabel })

          this.$bvModal
            .msgBoxOk(message, {
              title,
              size: 'md',
              okTitle,
              okVariant: 'primary',
              centered: true,
            })
            .then(() => {
              this.startAccountingConnect(program, authLink)
            })
            .catch(() => {})
        }
      } catch (e) {}
    },

    showConnectErrorModal() {
      this.$bvModal.msgBoxOk(this.$t('Message.Failed'), {
        title: this.$t('Message.Error'),
        size: 'md',
        okVariant: 'danger',
        centered: true,
      })
    },

    // Start accounting OAuth connect and redirect to returned URL
    async startAccountingConnect(program = 'fortnox', authLink = null) {
      const preferredProgram = this.normalizeAccountingProgram(program, 'fortnox')
      const candidates = [preferredProgram]
      if (preferredProgram !== 'fortnox') candidates.push('fortnox')
      if (preferredProgram !== 'spiris') candidates.push('spiris')

      const directLink = authLink || null
      if (directLink) {
        window.location.href = directLink
        return
      }

      for (let i = 0; i < candidates.length; i += 1) {
        const link = await this.fetchAuthLink(candidates[i])
        if (link) {
          window.location.href = link
          return
        }
      }

      this.showConnectErrorModal()
    },
  },
}
</script>
